import type { IProjectList } from "../types";
import type {
  IAnalyticsGroupProperties,
  IIdentifyAnalyticsUserProps,
  IProductAnalyticsClient,
  ISetAnalyticsGroupProps,
} from "./product-analytics.types";
import { buildAnalyticsPersonProperties } from "./product-analytics.utils";

// NOTE: The identity half of the PostHog lifecycle, as pure decisions rather
// than effects. Web and mobile each own a provider that runs the same three
// rules — identify once per person, group once per project, reset only on a
// real logout — and each had its own copy of them. Two copies of "only on a
// real logout" is two places for that subtlety to be lost.
//
// Deliberately free of React (this package forbids it, see the package
// CLAUDE.md): each app keeps its own `useEffect` and its own refs, and calls
// these to decide *what* to do. That also makes the rules testable without
// rendering anything.

// NOTE: Only the fields the analytics identity needs. Typing this structurally
// rather than importing a profile type keeps the rules independent of whichever
// user shape a given app hands in.
export interface IAnalyticsUser {
  uuid: string;
  email: string;
  firstName: string;
  lastName: string;
  isSsoUser: boolean;
  provider: string[];
  createdAt: string;
  activeSubscription?: { productCode: string } | null | undefined;
}

export interface IResolveAnalyticsGroupPropertiesProps {
  projects: IProjectList | null;
  projectUuid: string;
}

export interface IResolveAnalyticsIdentityActionProps {
  identifiedUserUuid: string | null;
  user: IAnalyticsUser | null;
}

export interface IResolveAnalyticsGroupActionProps {
  groupedProjectUuid: string | null;
  currentProjectUuid: string | null;
  projects: IProjectList | null;
  isAuthenticated: boolean;
}

export type TAnalyticsIdentityAction =
  | { type: "skip" }
  | { type: "identify"; identify: IIdentifyAnalyticsUserProps }
  | { type: "reset" };

export type TAnalyticsGroupAction =
  | { type: "skip" }
  | { type: "setGroup"; setGroup: ISetAnalyticsGroupProps };

// NOTE: Resolves the display name of the current project so PostHog's group
// timeline is readable, rather than a wall of uuids. Personal projects are
// flagged so self-serve and org usage can be separated — they behave very
// differently and blending them makes both numbers meaningless.
export const resolveAnalyticsGroupProperties = ({
  projects,
  projectUuid,
}: IResolveAnalyticsGroupPropertiesProps): IAnalyticsGroupProperties | null => {
  // NOTE: `null` rather than `undefined` so every path returns a value —
  // this package's `consistent-return` reads a bare `undefined` as no return.
  if (!projects) return null;

  if (projects.personalProject?.uuid === projectUuid) {
    return { name: projects.personalProject.title, is_personal: true };
  }

  for (const organization of projects.organizationProjects) {
    const match = organization.projects.find(
      (project) => project.uuid === projectUuid,
    );
    if (match) {
      return { name: match.title, is_personal: false };
    }
  }

  return null;
};

export const resolveAnalyticsIdentityAction = ({
  identifiedUserUuid,
  user,
}: IResolveAnalyticsIdentityActionProps): TAnalyticsIdentityAction => {
  if (user) {
    // NOTE: Auth refetches hand back the same person repeatedly; re-identifying
    // on each one is pure noise.
    if (identifiedUserUuid === user.uuid) return { type: "skip" };

    return {
      type: "identify",
      identify: {
        distinctId: user.uuid,
        personProperties: buildAnalyticsPersonProperties({
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName,
          isSsoUser: user.isSsoUser,
          providers: user.provider,
          subscriptionPlan: user.activeSubscription?.productCode ?? null,
          createdAt: user.createdAt,
        }),
      },
    };
  }

  // NOTE: Reset only on a *real* logout. The first render before the user query
  // resolves also has `user === null`, and resetting there would throw away the
  // anonymous id that stitches pre-signup activity onto the account — which is
  // what makes landing → signup → activation a single funnel.
  if (identifiedUserUuid !== null) return { type: "reset" };

  return { type: "skip" };
};

export const resolveAnalyticsGroupAction = ({
  groupedProjectUuid,
  currentProjectUuid,
  projects,
  isAuthenticated,
}: IResolveAnalyticsGroupActionProps): TAnalyticsGroupAction => {
  if (!isAuthenticated || !currentProjectUuid) return { type: "skip" };
  if (groupedProjectUuid === currentProjectUuid) return { type: "skip" };

  const groupProperties = resolveAnalyticsGroupProperties({
    projects,
    projectUuid: currentProjectUuid,
  });

  return {
    type: "setGroup",
    setGroup: {
      groupKey: currentProjectUuid,
      ...(groupProperties ? { groupProperties } : {}),
    },
  };
};

// NOTE: The bookkeeping each app used to keep in two refs. Held here as one
// object so the rule that a reset also clears the group lives in a single
// place — when they were separate refs in two providers, that coupling was
// implicit in four spots and easy to lose in a refactor.
export interface IAnalyticsLifecycleState {
  identifiedUserUuid: string | null;
  groupedProjectUuid: string | null;
}

export interface ISyncAnalyticsIdentityProps {
  state: IAnalyticsLifecycleState;
  user: IAnalyticsUser | null;
  client: IProductAnalyticsClient;
}

export interface ISyncAnalyticsGroupProps {
  state: IAnalyticsLifecycleState;
  currentProjectUuid: string | null;
  projects: IProjectList | null;
  isAuthenticated: boolean;
  client: IProductAnalyticsClient;
}

export const createAnalyticsLifecycleState = (): IAnalyticsLifecycleState => ({
  identifiedUserUuid: null,
  groupedProjectUuid: null,
});

// NOTE: Mutates `state` deliberately — the caller holds it in a ref, and this
// bookkeeping must not trigger a re-render on either platform.
export const syncAnalyticsIdentity = ({
  state,
  user,
  client,
}: ISyncAnalyticsIdentityProps): void => {
  const action = resolveAnalyticsIdentityAction({
    identifiedUserUuid: state.identifiedUserUuid,
    user,
  });

  if (action.type === "identify") {
    state.identifiedUserUuid = action.identify.distinctId;
    client.identify(action.identify);
    return;
  }

  if (action.type === "reset") {
    // NOTE: The group goes with the person. Leaving it set would attach the
    // next user on this device to the previous one's workspace.
    state.identifiedUserUuid = null;
    state.groupedProjectUuid = null;
    client.reset();
  }
};

export const syncAnalyticsGroup = ({
  state,
  currentProjectUuid,
  projects,
  isAuthenticated,
  client,
}: ISyncAnalyticsGroupProps): void => {
  const action = resolveAnalyticsGroupAction({
    groupedProjectUuid: state.groupedProjectUuid,
    currentProjectUuid,
    projects,
    isAuthenticated,
  });

  if (action.type !== "setGroup") return;

  state.groupedProjectUuid = action.setGroup.groupKey;
  client.setGroup(action.setGroup);
};
